/* -----------------Globals--------------- */
import { useState } from "react";
import { useTranslation } from "react-i18next";

/* -----------------Helpers & Hooks--------------- */
import { getProgressCardsData } from "@helpers/progress-data";

/* -----------------Child components--------------- */
import ProgressView from "../components/Progress/ProgressView";

/* -----------------Assets--------------- */
import ProgressEmptySvg from "../../../../assets/progress-empty-state.svg";

import { getShowStudyPackViews } from "@helpers/exams";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import { useDevice } from "@hooks/useDevice";
import { router } from "expo-router";

import { useExamContext } from "@features/exam/contexts/ExamContext";
import { SessionConfigDialog } from "@features/session-config/components/SessionConfigDialog";
import { useAppTheme } from "@hooks/useAppTheme";
import useMixedItemDist from "@hooks/useMixedItemDist";
import type { MixedItemDist } from "@memorang/types/src/tag";
import EmptyState from "@memorang/ui/src/components/EmptyView";
import Loader from "@memorang/ui/src/components/Loader";
import ScrollContainer from "@memorang/ui/src/components/ScrollContainer";
const ProgressScreen = () => {
	const { data: mixedItemDist, isLoading } = useMixedItemDist();

	if (isLoading || !mixedItemDist) {
		return <Loader />;
	}

	return <PreloadedProgressView mixedItemDist={mixedItemDist} />;
};

export default ProgressScreen;

const PreloadedProgressView = ({
	mixedItemDist,
}: {
	mixedItemDist: MixedItemDist;
}) => {
	const { t } = useTranslation();

	const [showSessionConfigDialog, setShowSessionConfigDialog] = useState(false);

	const toggleSessionConfigDialog = () => {
		setShowSessionConfigDialog(!showSessionConfigDialog);
	};

	const { handleSessionConfigNavigation } = useCustomNavigation();
	const { isMediumWindowSize } = useDevice();
	const {
		currentExamContext: { studyPackCollectionId },
	} = useExamContext();

	const theme = useAppTheme();
	const {
		masteryTagsProgressData,
		needsImprovementProgressData,
		progressPageSummaryCardData,
		practiceTestsCardsData,
		showEmptyState,
	} = getProgressCardsData(mixedItemDist, "Progress", true, t, theme);

	const showStudyPackViews = getShowStudyPackViews(studyPackCollectionId);

	return (
		<ScrollContainer
			contentContainerStyle={{ padding: 16 }}
			maxWidth={isMediumWindowSize ? "sm" : "md"}
			// biome-ignore lint/a11y/useSemanticElements: <explanation>
			role="main"
		>
			{showEmptyState ? (
				<EmptyState
					title={t("emptyState.progress.title")}
					subtitle={t("emptyState.progress.subtitle")}
					cta={t("emptyState.progress.cta")}
					image={ProgressEmptySvg}
					handleAction={() => {
						if (showStudyPackViews) {
							handleSessionConfigNavigation(toggleSessionConfigDialog);
						} else {
							router.push("/practice-tests");
						}
					}}
				/>
			) : (
				<ProgressView
					masteryTagsProgressData={masteryTagsProgressData}
					needsImprovementProgressData={needsImprovementProgressData}
					summaryCardData={progressPageSummaryCardData}
					practiceTestsCardData={practiceTestsCardsData}
					showStudyPackViews={showStudyPackViews}
				/>
			)}
			{showSessionConfigDialog ? (
				<SessionConfigDialog
					visible={showSessionConfigDialog}
					handleClose={toggleSessionConfigDialog}
				/>
			) : null}
		</ScrollContainer>
	);
};
